// Bilingual SEO metadata for fixed, code-only page titles/descriptions.
// These strings have no backend fields, so Arabic translations live here.
// Canonical URLs, share images and og:url ALWAYS stay English/canonical —
// Arabic metadata is browser-side only (admin preview) and never changes
// the crawler-visible initial HTML, which is baked at build time in English.

export interface PageSeoEntry {
  title: string;
  description: string;
  keywords: string;
}

export const seoDictionary: Record<string, { en: PageSeoEntry; ar: Partial<PageSeoEntry> }> = {
  home: {
    en: {
      title: "AI-Powered Personalized Fragrances",
      description:
        "Create your signature scent with ANAYA's AI-powered perfume creation. Answer a few questions about your essence and receive three personalized fragrances crafted just for you.",
      keywords:
        "personalized perfume, custom fragrance, AI perfume, luxury perfume, ANAYA, signature scent, bespoke fragrance",
    },
    ar: {
      title: "عطور شخصية مدعومة بالذكاء الاصطناعي",
      description:
        "ابتكر عطرك المميز مع تجربة ANAYA المدعومة بالذكاء الاصطناعي. أجب عن بضعة أسئلة عن ذوقك واحصل على ثلاثة عطور شخصية صُنعت خصيصاً لك.",
      keywords: "عطر شخصي, عطر مخصص, عطر فاخر, أنايا, عطر مميز, عطر حسب الطلب",
    },
  },
  quiz: {
    en: {
      title: "Create Your Signature Scent",
      description:
        "Take our personalized fragrance quiz and let AI craft the perfect perfume for your unique personality, style, and preferences.",
      keywords: "perfume quiz, fragrance finder, personalized scent quiz, custom perfume creator",
    },
    ar: {
      title: "ابتكر عطرك المميز",
      description:
        "شارك في اختبار العطور الشخصي ودع الذكاء الاصطناعي يصمم العطر المثالي لشخصيتك وأسلوبك وتفضيلاتك.",
      keywords: "اختبار العطور, اكتشاف العطر المناسب, عطر شخصي",
    },
  },
  corporateGifting: {
    en: {
      title: "Corporate Gifting",
      description:
        "Elevate your corporate events with bespoke fragrances. Custom perfumes and luxury packaging designed for VIP clients and corporate gifting.",
      keywords: "corporate gifting, business gifts, luxury corporate presents, custom branded perfume, VIP gifts",
    },
    ar: {
      title: "هدايا الشركات",
      description:
        "ارتقِ بمناسبات شركتك مع عطور مصممة خصيصاً. عطور مخصصة وتغليف فاخر لكبار العملاء وهدايا الشركات.",
      keywords: "هدايا الشركات, هدايا الأعمال, هدايا فاخرة للشركات, عطور مخصصة",
    },
  },
  specialOccasions: {
    en: {
      title: "Special Occasions",
      description:
        "Celebrate life's precious moments with bespoke fragrances. Perfect for weddings, anniversaries, baby showers, and milestones.",
      keywords: "wedding perfume, anniversary gift, special occasion fragrance, celebration gift, milestone gift",
    },
    ar: {
      title: "المناسبات الخاصة",
      description:
        "احتفل بأثمن لحظات الحياة مع عطور مصممة خصيصاً. مثالية للأعراس والذكرى السنوية ومناسبات استقبال المواليد والإنجازات.",
      keywords: "عطر الزفاف, هدية الذكرى السنوية, عطر المناسبات الخاصة, هدية احتفال",
    },
  },
  blog: {
    en: {
      title: "Blog",
      description: "Discover the art of fragrance creation, perfume tips, and the latest from ANAYA Perfume.",
      keywords: "perfume blog, fragrance tips, scent guide, perfume articles",
    },
    ar: {
      title: "المدونة",
      description: "اكتشف فن صناعة العطور ونصائح العطور وآخر أخبار ANAYA.",
      keywords: "مدونة العطور, نصائح العطور, دليل الروائح, مقالات العطور",
    },
  },
  contact: {
    en: {
      title: "Contact Us",
      description: "Get in touch with ANAYA Perfume. We're here to help with your fragrance journey.",
      keywords: "contact ANAYA, perfume support, fragrance help",
    },
    ar: {
      title: "تواصل معنا",
      description: "تواصل مع ANAYA Perfume. نحن هنا لمساعدتك في رحلتك العطرية.",
      keywords: "تواصل مع أنايا, دعم العطور, مساعدة العطور",
    },
  },
  howItWorks: {
    en: {
      title: "How It Works",
      description:
        "Learn how ANAYA creates your personalized fragrance using AI technology. From quiz to delivery, discover the journey of your signature scent.",
      keywords: "how AI perfume works, custom fragrance process, personalized scent creation",
    },
    ar: {
      title: "كيف نعمل",
      description:
        "تعرّف على كيفية ابتكار ANAYA لعطرك الشخصي باستخدام تقنية الذكاء الاصطناعي. من الاختبار حتى التوصيل، اكتشف رحلة عطرك المميز.",
      keywords: "كيف يعمل العطر بالذكاء الاصطناعي, عملية العطر المخصص, ابتكار عطر شخصي",
    },
  },
  about: {
    en: {
      title: "Who We Are",
      description:
        "Discover the story behind ANAYA Perfume. Learn about our mission to revolutionize personalized fragrance creation through AI technology.",
      keywords: "about ANAYA, perfume company, fragrance brand story, AI perfume technology",
    },
    ar: {
      title: "من نحن",
      description:
        "اكتشف قصة ANAYA Perfume. تعرّف على رسالتنا في إحداث ثورة في ابتكار العطور الشخصية عبر تقنية الذكاء الاصطناعي.",
      keywords: "عن أنايا, شركة عطور, قصة علامة عطرية",
    },
  },
};

export type SeoPageKey = keyof typeof seoDictionary;

/** Per-field fallback: Arabic only when present and non-blank. */
export function resolvePageSeo(key: SeoPageKey, language: "en" | "ar"): PageSeoEntry {
  const entry = seoDictionary[key] ?? seoDictionary.home;
  if (language !== "ar") return entry.en;
  return {
    title: entry.ar.title?.trim() || entry.en.title,
    description: entry.ar.description?.trim() || entry.en.description,
    keywords: entry.ar.keywords?.trim() || entry.en.keywords,
  };
}
